import React, { useEffect, useState } from "react";
import axiosAuth from "../utils/axiosAuth";
import { useAuth } from "../context/AuthContext";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { openAuthPopup } from "../utils/popupManager";
// PHASE 1: Import shared Gmail accounts hook (eliminates duplicate API calls)
import { useGmailAccounts } from "../hooks/useGmailAccounts";
import "./styles/ManageAccounts.css";

const ManageAccounts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalRemainingQuota, setTotalRemainingQuota] = useState(0);

  // PHASE 1 OPTIMIZATION: Use shared Gmail accounts hook instead of duplicate API call
  // This eliminates redundant /api/accounts-v2/current requests (also called in CreateCampaign)
  const { 
    data: gmailAccountsData, 
    isLoading: gmailAccountsLoading,
    refetch: refetchGmailAccounts,
    error: gmailAccountsError 
  } = useGmailAccounts(user?.uid, { enabled: !!user });

  // Update local state when hook data changes
  useEffect(() => {
    if (gmailAccountsData) {
      setConnectedAccounts(gmailAccountsData.accounts);
      setTotalRemainingQuota(gmailAccountsData.totalQuota);
      setLoading(false);
      setError("");
      console.log("✅ [ManageAccounts] Gmail v2 - Data loaded from shared hook:", {
        accounts: gmailAccountsData.accounts.length,
        totalQuota: gmailAccountsData.totalQuota
      });
    }
  }, [gmailAccountsData]);

  // Update loading state
  useEffect(() => {
    setLoading(gmailAccountsLoading);
  }, [gmailAccountsLoading]);

  // Handle Gmail accounts loading errors
  useEffect(() => {
    if (gmailAccountsError) {
      console.error("❌ [ManageAccounts] Gmail v2 - Fetch error:", gmailAccountsError);
      
      if (gmailAccountsError.response?.status === 404) {
        setError("Gmail v2 API endpoint not found. Please check your API configuration.");
      } else if (gmailAccountsError.response?.status === 401) {
        setError("Unauthorized: Invalid token. Please log in again.");
      } else if (gmailAccountsError.code === 'NETWORK_ERROR' || !gmailAccountsError.response) {
        setError("Network error: Unable to connect to server. Please check your internet connection.");
      } else {
        setError(`Failed to fetch Gmail v2 account info: ${gmailAccountsError.response?.data?.error || gmailAccountsError.message}`);
      }
      setLoading(false);
    }
  }, [gmailAccountsError]);

  const handleBack = () => {
    navigate(-1);
  };

  const getAccountStatus = (account) => {
    if (account?.needsReauth || account?.sendingError === 'gmail_auth' || account?.sendingPaused) {
      return { label: 'Reconnect required', className: 'needs-reauth' };
    }
    if (account?.isAuthorized === false) {
      return { label: 'Unauthorized', className: 'unauthorized' };
    }
    return { label: 'Authorized ✓', className: 'authorized' };
  };

  const addAccount = async () => {
    if (!user) {
      alert("Please log in first");
      return;
    }

    const token = await user.getIdToken(true);
    localStorage.setItem("authToken", token);

    const url = `${process.env.REACT_APP_API_URL || 'https://api.fans.nextremedy.net'}/api/auth-v2/google?mainUserToken=${token}`;
    openAuthPopup(url, 'manage-accounts');
    
    // ✅ MEO PATTERN: No popup error check - browser handles popup blockers naturally
  };

  const handleRemoveAccount = async (emailToRemove) => {
    // ✅ MEO PATTERN: No confirmation popup - cleaner UX
    try {
      const token = await user.getIdToken(true);
      localStorage.setItem("authToken", token);

      console.log("🗑️ Gmail v2 - Removing account:", emailToRemove);
      
      // Optimistic UI update (like MEO)
      setConnectedAccounts((prev) => prev.filter((acc) => acc.email !== emailToRemove));
      
      const response = await axiosAuth.post("/api/accounts-v2/remove-gmail-account", {
        email: emailToRemove,
      });

      if (response.data.success) {
        console.log("✅ Gmail v2 - Account removed:", response.data);
        refetchGmailAccounts(); // PHASE 1: Use shared hook refetch
        alert("Gmail account removed successfully!");
      } else {
        throw new Error(response.data.error || "Failed to remove account");
      }
    } catch (err) {
      console.error("❌ Gmail v2 - Remove error:", err);
      alert(`Failed to remove Gmail account: ${err.response?.data?.error || err.message}`);
      // ✅ MEO PATTERN: Revert optimistic update on error
      refetchGmailAccounts();
    }
  };

  // PHASE 1: Gmail accounts now loaded automatically via shared hook
  // No manual fetch needed on mount - the hook handles it
  useEffect(() => {
    if (!user) {
      setLoading(false);
      setError("Please log in first");
    }
  }, [user]);

  // ✅ Gmail v2 - Handle popup messages (PHASE 1: use shared hook refetch)
  useEffect(() => {
    const handleMessage = (event) => {
      console.log("📥 Gmail v2 - Message received from popup:", event.data, "from origin:", event.origin);
      
      if (event.data === "gmail_v2_account_added") {
        console.log("✅ Gmail v2 - Account added successfully, refreshing via shared hook...");
        refetchGmailAccounts().then(() => {
          alert("✅ Gmail account added successfully!");
        }).catch(err => {
          console.error("❌ Failed to refresh accounts:", err);
          alert("Account added but failed to refresh list. Please try again.");
        });
      } else if (event.data === "gmail_v2_account_add_error") {
        console.log("❌ Gmail v2 - Account add failed");
        alert("❌ Failed to add Gmail account. Please try again.");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [refetchGmailAccounts]); // PHASE 1: Use shared hook refetch

  const getInitials = (email) => {
    if (!email || typeof email !== "string") return "?";
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="manage-accounts-container">
          <div className="manage-accounts-header">
            <button className="back-button" onClick={handleBack}>
              <ArrowLeftOutlined /> Back
            </button>
            <h1>Manage Gmail Accounts</h1>
            <p className="quota-info">
              Quota: {totalRemainingQuota} / 40 per day
            </p>
          </div>

          <div className="accounts-section">
            <div className="accounts-section-header">
              <div className="accounts-section-title">
                Gmail Accounts
                <span className="account-count">{connectedAccounts.length}</span>
              </div>
            </div>

            <div className="accounts-list">
              {loading ? (
                <div className="loading-spinner">Loading accounts...</div>
              ) : error ? (
                <p className="error">{error}</p>
              ) : (
                Array.isArray(connectedAccounts) &&
                connectedAccounts
                  .filter(account => account && account.email)
                  .map((account) => (
                    <div key={account.email} className="account-item">
                      <div className="account-info">
                        <div className="account-avatar">{getInitials(account.email)}</div>
                        <span className="account-email">{account.email}</span>
                      </div>
                      <div className="account-actions">
                        {(() => {
                          const status = getAccountStatus(account);
                          return (
                            <>
                              <span className={`account-status ${status.className}`}>{status.label}</span>
                              {status.className === 'needs-reauth' && (
                                <button className="add-button" style={{ marginLeft: '8px' }} onClick={addAccount}>
                                  Reconnect
                                </button>
                              )}
                            </>
                          );
                        })()}
                        <button className="remove-button" onClick={() => handleRemoveAccount(account.email)}>×</button>
                      </div>
                      <div className="quota-info">
                        <span>Emails remaining today: {account.quotaRemaining || 0}/40</span>
                        <span>Resets: Daily at UTC midnight</span>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

          <div className="add-account-section">
            <div className="add-account-form">
              <button
                className="add-button"
                onClick={addAccount}
                disabled={loading}
              >
                <PlusOutlined /> Add Account
              </button>
            </div>
          </div>
          
          {loading && (
            <div className="loading-popup-message">
              ⏳ Waiting for Google authorization to complete...
            </div>
          )}

          <div className="notes-card">
            <div className="notes-list">
              <div className="notes-list-item">
                • You can use the added account to send Campaigns emails
              </div>
              <div className="notes-list-item">
                • Check the profile in the sidebar to ensure the correct account is selected
              </div>
              <div className="notes-list-item">
                • All email campaigns will be sent from the selected account
              </div>
              <div className="notes-list-item">
                • Make sure the account has Gmail API access enabled
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageAccounts;
